import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface SignInFormProps {
  onModeChange: (mode: "signup" | "reset") => void;
}

export const SignInForm = ({ onModeChange }: SignInFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showAccountExistsHint, setShowAccountExistsHint] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setShowAccountExistsHint(false);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email.trim(),
        password: formData.password,
      });

      if (error) {
        console.error("Sign in error:", error);
        
        if (error.message === "Email not confirmed") {
          setShowAccountExistsHint(true);
          toast({
            title: "Email Not Confirmed",
            description: "Please check your email for a verification link. Need a new link? Click 'Forgot your password?' below.",
            variant: "destructive",
          });
          return;
        }
        
        if (error.message === "Invalid login credentials") {
          setShowAccountExistsHint(true);
          throw new Error(
            "Incorrect email or password. Please try again or reset your password using the link below."
          );
        }
        
        if (error.message?.includes("rate limit") || error.status === 429) {
          throw new Error("Too many login attempts. Please try again in 60 minutes.");
        }
        
        throw new Error("An error occurred while signing in. Please try again.");
      }

      if (data.user) {
        // Check if user is admin and is carynjosepher@gmail.com
        const { data: isAdmin } = await supabase.rpc('has_role', { 
          role_to_check: 'admin' 
        });

        toast({
          title: "Welcome back!",
          description: "You have successfully signed in.",
        });

        // Redirect to admin dashboard if user is admin AND has the specific email
        if (isAdmin && data.user.email === 'carynjosepher@gmail.com') {
          navigate("/admin");
        } else {
          navigate("/dashboard");
        }
      }
    } catch (error: any) {
      console.error("Sign in error details:", error);
      
      let errorMessage = error.message;
      
      if (error?.status === 429 || error?.message?.includes("rate limit")) {
        errorMessage = "Too many attempts. Please wait 60 minutes before trying again.";
      }
      
      toast({
        title: "Error",
        description: errorMessage || "Failed to sign in. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={formData.email}
            onChange={(e) => {
              setFormData({ ...formData, email: e.target.value });
              setShowAccountExistsHint(false);
            }}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={formData.password}
            onChange={(e) => {
              setFormData({ ...formData, password: e.target.value });
              setShowAccountExistsHint(false);
            }}
            required
          />
        </div>
      </div>

      {showAccountExistsHint && (
        <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md">
          Need help accessing your account?
          <br />
          1. Click "Forgot your password?" below
          <br />
          2. Enter your email to receive a password reset link
          <br />
          3. Check your inbox (and spam folder) for the reset link
        </div>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? "Signing in..." : "Sign In"}
      </Button>

      <div className="mt-4 text-center space-y-2">
        <button
          type="button"
          onClick={() => onModeChange("reset")}
          className="text-sm text-blue-600 hover:underline block w-full font-medium"
        >
          Forgot your password?
        </button>
        <button
          type="button"
          onClick={() => onModeChange("signup")}
          className="text-sm text-blue-600 hover:underline block w-full"
        >
          Don't have an account? Sign up
        </button>
      </div>
    </form>
  );
};
